import { createContext, useState, useEffect } from 'react';
import './config/aws-config';
import { useDispatch, useSelector } from 'react-redux';
import { checkAuthState } from './store/authSlice';
import { ApolloProvider } from '@apollo/client';
import { client } from './services/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useSearchStore } from './store/searchStore';
import { StudioErstellenProvider } from './store/studioErstellenStore';
import { ServiceErstellenProvider } from './store/serviceErstellenStore';
import { sanitizeLegacyBookingDataStorage } from './store/bookingStore';
import CookieConsentBanner from './components/CookieConsent/CookieConsentBanner';
import ProtectedRoute from './components/ProtectedRoute';
// import AuthRedirectRoute from './components/AuthRedirectRoute';
import CreativeOwnerRoute from './components/CreativeOwnerRoute';
import StudioOwnerRoute from './components/StudioOwnerRoute';
import BookingRoleRoute from './components/BookingRoleRoute';
import ErrorBoundary from './util/ErrorBoundary';
import { RemoveScroll } from 'react-remove-scroll';
import { useThemeColor } from './hooks/useThemeColor';
import { useTrackingPageViews } from './hooks/useTrackingPageViews';
import ScrollToTopOnRouteChange from './util/ScrollToTopRouter';

import './App.css';

import HomePage from './pages/HomePage/HomePage';
import StudioProfile from './pages/StudioProfile/Studio';
import StudioMessagePage from './pages/StudioMessagePage/StudioMessagePage';
import SearchPage from "./pages/SearchPage/SearchPage"
import UserProfile from './pages/UsersPage/UserProfile';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import CreatorPage from './pages/CreatorPage/CreatorPage';
import BookingPage from './pages/BookingsPage/BookingPage';
import BookingDetailsPage from './pages/BookingsPage/BookingDetails/BookingDetails';
import CreatorBookingsDetails from './pages/CreatorPage/CreatorBookingsDetails/CreatorBookingsDetails';
import FavouritePage from './pages/FavouritePage/FavouritePage';
import Wishlist from './pages/FavouritePage/Wishlist/Wishlist';
import MessagesPage from './pages/MessagesPage/MessagesPage';
import CalendarPage from './pages/CalendarPage/CalendarPage';
import ServicesStudiosPage from './pages/ServicesStudiosPage/ServicesStudiosPage';
import DatenschutzPage from './pages/DatenschutzPage/Datenschutz';
import CookieSettingsPage from './pages/CookieSettingsPage/CookieSettingsPage';
import AgbPage from './pages/AgbPage/AgbPage';
import Kontakt from './pages/KontaktPage/Kontakt';
import ImpressumPage from './pages/ImpressumPage/ImpressumPage';
import StudioEditorPage from './pages/StudioEditorPage/StudioEditorPage';
import PreviewStudio from './pages/PreviewStudio/PreviewStudio';
import FinanceDashboard from './pages/FinancePage/FinanceDashboard';
import FinanceMenu from './pages/FinancePage/FinanceMenu';
import Income from './pages/FinancePage/Income';
import EmpfehlungsPage from './pages/EmpfehlungsPage/EmpfehlungsPage';
import RightSide from './components/EmpfelungsPage/RightSide';
import PayoutDashboard from './pages/FinancePage/PayoutDashboard';
import Receipt from './components/ProfilePage/Receipt';
import StripeReturn from './pages/FinancePage/StripeReturn';
import RegisterPhone from './components/RegisterPhone';
import SignInPhone from './components/SignInPhone';
import AccountDetails from './components/ProfilePage/Phone/AccountDetails';
import OAuthRedirectHandler from './components/OAuthRedirectHandler';
import ProfileCompletionModal from './components/ProfileCompletionModal';
import NotificationHost from './components/UI/NotificationHost/NotificationHost';
import { clearLegacyBookingPaymentDraftStorage } from './util/stripeBookingPaymentFlow';

import StudioErstellen from './components/StudioErstellen/StudioErstellen';
import UnauthorizedError from './pages/ErrorPages/401Error';
import ForbiddenError from './pages/ErrorPages/403Error';
import NotFoundError from './pages/ErrorPages/404Error';
import ServerError from './pages/ErrorPages/500Error';
import ServiceUnavailableError from './pages/ErrorPages/503Error';
import MemberPage from './pages/MemberPage/MemberPage';
import ServiceErstellenPage from './pages/ServiceErstellenPage/ServiceErstellenPage';
import CreativeEditingPage from './pages/CreativeEditingPage/CreativeEditingPage';

export const MapContext = createContext();
export const NavBarContext = createContext();
export const SearchContext = createContext();

const queryClient = new QueryClient();

// Component to handle theme color changes inside Router context
function TrackingPageViews() {
  useTrackingPageViews();
  return null;
}

function AppRoutes() {
  useThemeColor(); // This hook manages the browser theme color

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/studio/:id" element={<StudioProfile />} />
        <Route path="/studio/:id/nachricht" element={<StudioMessagePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/search/:params" element={<SearchPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />
        <Route path="/cookie-einstellungen" element={<CookieSettingsPage />} />
        <Route path="/agb" element={<AgbPage />} />
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/kontakt" element={<Kontakt />} />
        <Route path="/members/:id" element={<MemberPage />} />
        <Route path="/members/:id/:tab" element={<MemberPage />} />
        <Route path="/members/:id/project/:projectId/overview" element={<MemberPage />} />
        <Route path="/empfehlungs" element={<EmpfehlungsPage />} />
        <Route path="/empfehlung/form" element={<RightSide />} />
        <Route 
          path="/anmelden" 
          element={
            // <AuthRedirectRoute>
              <SignInPhone />
            // </AuthRedirectRoute>
          } 
        />
        <Route 
          path="/registrieren" 
          element={
            // <AuthRedirectRoute>
              <RegisterPhone />
            // </AuthRedirectRoute>
          } 
        />
        <Route 
          path="/creator" 
          element={
            <ProtectedRoute requireStudio>
              <CreatorPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/studio-erstellen" 
          element={
            <ProtectedRoute>
              <StudioErstellen />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/studio-erstellen/:step" 
          element={
            <ProtectedRoute>
              <StudioErstellen />
            </ProtectedRoute>
          } 
        />
        {/* Params page here */}
        <Route path="/users/show/:id" element={<UserProfile />} />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/bookings"
          element={
            <ProtectedRoute>
              <BookingPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/bookings/details/:id"
          element={
            <ProtectedRoute>
              <BookingRoleRoute allowedRole="guest">
                <BookingDetailsPage />
              </BookingRoleRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/creator/details/:id"
          element={
            <ProtectedRoute requireStudio>
              <BookingRoleRoute allowedRole="owner">
                <CreatorBookingsDetails />
              </BookingRoleRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/wishlists"
          element={
            <ProtectedRoute>
              <FavouritePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/wishlists/:id"
          element={
            <ProtectedRoute>
              <Wishlist />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/messages"
          element={
            <ProtectedRoute>
              <MessagesPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/messages/:id"
          element={
            <ProtectedRoute>
              <MessagesPage />
            </ProtectedRoute>
          } 
        />                        
        <Route 
          path="/calendar"
          element={
            <ProtectedRoute requireStudio>
              <CalendarPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/finanzen"
          element={
            <ProtectedRoute requireStudio>
              <FinanceMenu />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finanzen/dashboard"
          element={
            <ProtectedRoute requireStudio>
              <FinanceDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finanzen/income"
          element={
            <ProtectedRoute requireStudio>
              <Income />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finanzen/payouts"
          element={
            <ProtectedRoute requireStudio>
              <PayoutDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/receipt/:bookingId"
          element={
            <ProtectedRoute>
              <Receipt />
            </ProtectedRoute>
          }
        />
        <Route path="/stripe-return" element={<StripeReturn />} />
        <Route 
          path="/services-studios"
          element={
            <ProtectedRoute requireStudio>
              <ServicesStudiosPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/services-studios/editor/:id"
          element={
            <ProtectedRoute requireStudio>
              <StudioOwnerRoute>
                <StudioEditorPage />
              </StudioOwnerRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/creative-editing/:id/profile"
          element={
            <ProtectedRoute requireStudio>
              <CreativeOwnerRoute>
                <CreativeEditingPage />
              </CreativeOwnerRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/creative-editing/:id/view-project/:projectId"
          element={
            <ProtectedRoute requireStudio>
              <CreativeOwnerRoute>
                <CreativeEditingPage />
              </CreativeOwnerRoute>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/creative-editing/:id/project/:projectId/overview"
          element={
            <ProtectedRoute requireStudio>
              <CreativeOwnerRoute>
                <CreativeEditingPage />
              </CreativeOwnerRoute>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/creative-editing/:id/project/:projectId"
          element={
            <ProtectedRoute requireStudio>
              <CreativeOwnerRoute>
                <CreativeEditingPage />
              </CreativeOwnerRoute>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/creative-editing/:id/project"
          element={
            <ProtectedRoute requireStudio>
              <CreativeOwnerRoute>
                <CreativeEditingPage />
              </CreativeOwnerRoute>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/creative-editing/:id/package/:packageId"
          element={
            <ProtectedRoute requireStudio>
              <CreativeOwnerRoute>
                <CreativeEditingPage />
              </CreativeOwnerRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/creative-editing/:id/package"
          element={
            <ProtectedRoute requireStudio>
              <CreativeOwnerRoute>
                <CreativeEditingPage />
              </CreativeOwnerRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/creative-editing/:id"
          element={
            <ProtectedRoute requireStudio>
              <CreativeOwnerRoute>
                <CreativeEditingPage />
              </CreativeOwnerRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/creative-editing/:id/:tab"
          element={
            <ProtectedRoute requireStudio>
              <CreativeOwnerRoute>
                <CreativeEditingPage />
              </CreativeOwnerRoute>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/preview/studio/:id"
          element={
            <ProtectedRoute>
              <PreviewStudio />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile/account-details"
          element={
            <ProtectedRoute>
              <AccountDetails />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile/account-details/:section"
          element={
            <ProtectedRoute>
              <AccountDetails />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/services-erstellen" 
          element={
            <ProtectedRoute requireStudio>
              <ServiceErstellenProvider>
                <ServiceErstellenPage />
              </ServiceErstellenProvider>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/services-erstellen/:step" 
          element={
            <ProtectedRoute requireStudio>
              <ServiceErstellenProvider>
                <ServiceErstellenPage />
              </ServiceErstellenProvider>
            </ProtectedRoute>
          } 
        />
        {/* Error Routes - Add these at the end */}
        <Route path="/error/401" element={<UnauthorizedError />} />
        <Route path="/error/403" element={<ForbiddenError />} />
        <Route path="/error/500" element={<ServerError />} />
        <Route path="/error/503" element={<ServiceUnavailableError />} />
        
        {/* Catch-all 404 route - MUST be last */}
        <Route path="*" element={<NotFoundError />} />
      </Routes>
    </div>
  );
}

function App() {
  const [showMap, setShowMap] = useState(false);
  const [hideTopPage, setHideTopPage] = useState(false);
  const [studiosArrayIsLoading, setStudiosArrayIsLoading] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [activeView, setActiveView] = useState("explorer");
  const [activeButton, setActiveButton] = useState("");
  const [showRegister, setShowRegister] = useState(false);
  const [isPhoneSize, setIsPhoneSize] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const isPhone = window.innerWidth < 742;
      setIsPhoneSize(isPhone);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(checkAuthState());
  }, [dispatch]);

  useEffect(() => {
    clearLegacyBookingPaymentDraftStorage();
    sanitizeLegacyBookingDataStorage();
  }, []);

  const searchStore = useSearchStore();

  return (
    <ApolloProvider client={client}>
      <QueryClientProvider client={queryClient}>
        <SearchContext.Provider value={searchStore}>
          <NavBarContext.Provider value={{ 
            activeView, setActiveView, 
            showSignIn, setShowSignIn, 
            showRegister, setShowRegister, 
            isPhoneSize, setIsPhoneSize,
            activeButton, setActiveButton
          }}>
            <MapContext.Provider 
              value=
              {{ 
                showMap, setShowMap, 
                hideTopPage, setHideTopPage, 
                studiosArrayIsLoading, setStudiosArrayIsLoading,
                showSignIn, setShowSignIn,
                showRegister, setShowRegister,
                isPhoneSize, setIsPhoneSize 
              }}>
              <StudioErstellenProvider>
                <Router>
                  <ErrorBoundary fallback={<ServerError />}>
                    <RemoveScroll enabled={(showSignIn || showRegister) && !isAuthenticated}>
                      <TrackingPageViews />
                      <ScrollToTopOnRouteChange />
                      <OAuthRedirectHandler />
                      <ProfileCompletionModal />
                      <NotificationHost />
                      <AppRoutes />
                      <CookieConsentBanner />
                    </RemoveScroll>
                  </ErrorBoundary>
                </Router>
              </StudioErstellenProvider>
            </MapContext.Provider>
          </NavBarContext.Provider>
        </SearchContext.Provider>
      </QueryClientProvider>
    </ApolloProvider>
  );
}

export default App;
